"use client";
import React from "react";
import { skillsData } from "@/data/skill.data";
import SkillClient from "./SkillClient";
import SectionHeader from "@/components/shared/headers/SectionHeader";
import { useDict } from "@/components/providers/LocaleProvider";
import {
  FiLayers,
  FiCode,
  FiDatabase,
  FiPenTool,
} from "react-icons/fi";

/**
 * Coarse buckets used to render the category chips. The matching is done
 * by name (case-sensitive) against `skillsData`, so the count badge stays
 * in sync if a skill is added/removed without touching this file.
 */
const SKILL_CATEGORIES = [
  {
    key: "frontend" as const,
    icon: FiLayers,
    names: ["JavaScript", "TypeScript", "React", "Next.js", "Redux"],
  },
  {
    key: "backend" as const,
    icon: FiCode,
    names: [
      "Node.js",
      "Express.js",
      "NestJS",
      "GraphQL",
      "Socket.io",
      "JSON Web Tokens",
    ],
  },
  {
    key: "data" as const,
    icon: FiDatabase,
    names: [
      "MongoDB",
      "MySQL",
      "PostgreSQL",
      "Firebase",
      "Redis",
      "Prisma",
    ],
  },
  {
    key: "design" as const,
    icon: FiPenTool,
    names: ["Figma", "Adobe Illustrator", "Adobe Photoshop", "Canva"],
  },
];

const Skill = () => {
  const t = useDict();

  return (
    <section className="grid grid-cols-1 xl:grid-cols-2 gap-10 justify-center items-center">
      {/* LEFT — text panel */}
      <div className="space-y-6 order-2 xl:order-1">
        {/* Eyebrow pill with pulsing dot */}
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-text-primary-ligt">
          <span className="relative flex size-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex size-1.5 rounded-full bg-primary" />
          </span>
          {t.home.skills.eyebrow}
        </div>

        <SectionHeader
          title={t.home.skills.title}
          description={t.home.skills.description}
          align="left"
        />

        {/* Category chips — icon + label only, no counts */}
        <div className="flex flex-wrap gap-2">
          {SKILL_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const label = t.home.skills.categories[cat.key];
            return (
              <span
                key={cat.key}
                className="inline-flex items-center gap-1.5 rounded-full border border-light-border/30 bg-dark-gray/60 px-3 py-1.5 text-xs text-light-text transition-colors hover:border-primary/50 hover:text-text"
              >
                <Icon className="size-3.5 text-primary" />
                {label}
              </span>
            );
          })}
        </div>
      </div>

      {/* RIGHT — orbiting globe of tech icons */}
      <div className="order-1 xl:order-2 w-full">
        <SkillClient skillsData={skillsData} />
      </div>
    </section>
  );
};

export default Skill;
